import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

// Last known logged-in user — lets the app open while offline (PWA).
// SECURITY: only non-sensitive profile display data (name/email/picture) is
// stored here. The session token itself is an httpOnly cookie and is never
// readable by JavaScript; restoring this cache grants no server access.
const USER_CACHE_KEY = "julia_user";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not logged in

  useEffect(() => {
    // CRITICAL: if returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      // Keep as null so the loader stays visible until AuthCallback resolves
      return;
    }
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch((err) => {
        if (!err.response) {
          // Network error (offline) — restore the cached session so the app keeps working
          try {
            const cached = localStorage.getItem(USER_CACHE_KEY);
            if (cached) {
              setUser(JSON.parse(cached));
              return;
            }
          } catch {
            /* corrupt cache — fall through to logged-out */
          }
        }
        try { localStorage.removeItem(USER_CACHE_KEY); } catch { /* ignore */ }
        setUser(false);
      });
  }, []);

  // Keep the offline session cache up to date on every login / profile change.
  useEffect(() => {
    if (user && typeof user === "object") {
      try { localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user)); } catch { /* storage full */ }
    }
  }, [user]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      try { localStorage.removeItem(USER_CACHE_KEY); } catch { /* ignore */ }
      setUser(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (err) {
      if (!err.response) return null; // offline — keep the current session
      try { localStorage.removeItem(USER_CACHE_KEY); } catch { /* ignore */ }
      setUser(false);
      return null;
    }
  }, []);

  const value = useMemo(() => ({ user, setUser, logout, refresh }), [user, logout, refresh]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
